import React, { useState } from 'react';
import {
  ChevronDown, ChevronUp,
  Truck, Leaf, CreditCard, MessageCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const categoryIcon = (name) => {
  const cls = 'w-5 h-5';
  switch (name) {
    case 'About Book My Fresh':    return <Leaf className={cls} />;
    case 'Ordering & Delivery':    return <Truck className={cls} />;
    case 'Payments & Refunds':     return <CreditCard className={cls} />;
    case 'Complaints & Support':   return <MessageCircle className={cls} />;
    default:                       return null;
  }
};

const faqCategories = [
  {
    category: 'About Book My Fresh',
    faqs: [
      {
        question: 'What is Book My Fresh?',
        answer: 'Book My Fresh is an online platform that delivers fresh fruits and vegetables directly from farms and trusted suppliers to customers\' doorsteps, ensuring quality, freshness, and convenience.',
      },
      {
        question: 'What products are available on Book My Fresh?',
        answer: 'We offer a wide range of fresh fruits, vegetables, seasonal produce, and selected organic products based on availability.',
      },
      {
        question: 'Do you offer organic fruits and vegetables?',
        answer: 'Yes. Selected organic fruits and vegetables are available based on seasonality and supplier availability.',
      },
      {
        question: 'How do you ensure product freshness?',
        answer: 'Our produce is sourced directly from farms and trusted suppliers, carefully inspected, and delivered through a streamlined supply chain to maintain freshness and quality.',
      },
      {
        question: 'Why should I choose Book My Fresh?',
        answer: 'Book My Fresh offers farm-fresh fruits and vegetables with direct farm-to-home delivery, quality-checked produce, convenient ordering through our app and website, reliable delivery service, secure payments, and dedicated customer support.',
      },
      {
        question: 'Does Book My Fresh reserve the right to modify its services?',
        answer: 'Yes. Book My Fresh may update product availability, delivery areas, pricing, delivery schedules, policies, and service offerings from time to time to improve customer experience and operational efficiency.',
      },
    ],
  },
  {
    category: 'Ordering & Delivery',
    faqs: [
      {
        question: 'How do I place an order?',
        answer: 'You can place an order through the Book My Fresh mobile application and website by selecting your desired products, adding them to your cart, and completing the checkout process.',
      },
      {
        question: 'Do you provide same-day or instant delivery?',
        answer: 'Yes. We offer instant delivery in selected serviceable pincodes.',
      },
      {
        question: 'What are your delivery timings?',
        answer: 'Delivery timings depend on your location. Instant Delivery is available in selected pincodes from 6:00 AM to 11:00 PM. Next-Day Delivery is available in selected pincodes between 5:00 AM and 9:00 AM on the following day. Delivery slots may vary depending on operational requirements and service availability.',
      },
      {
        question: 'Is there a minimum order value?',
        answer: 'There is no minimum order value for Instant delivery — you can place an order of any size. For Scheduled delivery, a minimum order value may apply based on your location and is displayed at checkout.',
      },
      {
        question: 'Are there any delivery charges?',
        answer: 'Delivery charges may apply based on your location and order value, as displayed at checkout before you pay.',
      },
      {
        question: 'How can I track my order?',
        answer: 'You can track your order status directly through the Book My Fresh application after your order has been confirmed.',
      },
      {
        question: 'Can I cancel my order?',
        answer: 'Orders may be cancelled before they are processed or dispatched. Once an order has been dispatched, cancellation may not be possible.',
      },
    ],
  },
  {
    category: 'Payments & Refunds',
    faqs: [
      {
        question: 'Is online payment secure?',
        answer: 'Yes. All payments are processed through secure and trusted payment gateways that use industry-standard security measures.',
      },
      {
        question: 'How do refunds work?',
        answer: 'After investigating and approving a refund request: BMF Coins refunds are credited to your BMF Coins balance within 1 working day after complaint resolution. Original Payment Method Refunds are processed within 4–5 working days and may take additional time depending on your bank or payment provider.',
      },
      {
        question: 'Can I choose between BMF Coins refunds and bank refunds?',
        answer: 'Where applicable, customers may be offered a refund to their BMF Coins balance or to the original payment method, subject to company policies and the nature of the refund request.',
      },
    ],
  },
  {
    category: 'Complaints & Support',
    faqs: [
      {
        question: 'What should I do if I receive damaged, spoiled, missing, or incorrect products?',
        answer: 'If you receive damaged, spoiled, missing, or incorrect items, please contact our customer support team within 24 hours of delivery. Our team will investigate the issue and provide an appropriate resolution.',
      },
      {
        question: 'How long does it take to resolve a complaint?',
        answer: 'Most complaints are reviewed and resolved within 48 hours after receiving all required details and supporting information from the customer.',
      },
      {
        question: 'How can I contact customer support?',
        answer: 'You can contact our customer support team at: Email: info@bookmyfresh.com | Phone: +91 8078622096.',
      },
      {
        question: 'How can I provide feedback or suggestions?',
        answer: 'We value customer feedback. You can share your suggestions, reviews, or concerns by contacting our customer support team through email or phone.',
      },
    ],
  },
];

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [openIndex, setOpenIndex] = useState(null);

  const handleCategoryChange = (idx) => {
    setActiveCategory(idx);
    setOpenIndex(null);
  };

  const toggleFaq = (faqIndex) => {
    setOpenIndex(openIndex === faqIndex ? null : faqIndex);
  };

  const selected = faqCategories[activeCategory];

  return (
    <div className="faq-page">
      {/* Hero */}
      <section
        className="relative min-h-[42vh] flex items-center justify-center mt-16"
        style={{ background: 'linear-gradient(135deg, #2e7d32 0%, #548B2E 50%, #33691e 100%)' }}
      >
        <div className="relative z-10 text-center text-white px-4">
          <p className="text-[#bddd97] uppercase tracking-[0.3em] text-sm mb-4 font-medium">Help Center</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Frequently Asked Questions</h1>
          <div className="w-24 h-1 bg-[#bddd97] mx-auto mb-6" />
          <p className="text-green-100 text-lg max-w-xl mx-auto">
            Find answers by topic — shopping, delivery, freshness, deals, and more.
          </p>
        </div>
      </section>

      {/* Category overview cards */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <p className="text-center text-[#548B2E] uppercase tracking-[0.2em] text-sm mb-2 font-medium">Browse by Topic</p>
          <h2 className="text-center text-2xl md:text-3xl font-bold text-gray-800 mb-8">
            What can we help you with?
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {faqCategories.map((cat, idx) => (
              <button
                key={cat.category}
                onClick={() => {
                  handleCategoryChange(idx);
                  document.getElementById('faq-content')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 text-center
                  ${activeCategory === idx
                    ? 'border-[#548B2E] bg-[#f1f8e9] text-[#548B2E]'
                    : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-[#548B2E] hover:bg-[#f1f8e9] hover:text-[#548B2E]'
                  }`}
              >
                <span className={activeCategory === idx ? 'text-[#548B2E]' : 'text-gray-400'}>
                  {categoryIcon(cat.category)}
                </span>
                <span className="text-xs font-semibold leading-tight">{cat.category}</span>
                <span className="text-xs text-gray-400">{cat.faqs.length} questions</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main content — sidebar nav + FAQ panel */}
      <section id="faq-content" className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-8">

            {/* Sidebar — desktop */}
            <aside className="hidden md:block w-64 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Categories</p>
                </div>
                <nav className="py-2">
                  {faqCategories.map((cat, idx) => (
                    <button
                      key={cat.category}
                      onClick={() => handleCategoryChange(idx)}
                      className={`w-full flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors duration-150 text-left
                        ${activeCategory === idx
                          ? 'bg-[#f1f8e9] text-[#548B2E] border-r-4 border-[#548B2E]'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-[#548B2E]'
                        }`}
                    >
                      <span className={activeCategory === idx ? 'text-[#548B2E]' : 'text-gray-400'}>
                        {categoryIcon(cat.category)}
                      </span>
                      <span>{cat.category}</span>
                      <span className="ml-auto text-xs text-gray-300 font-normal">{cat.faqs.length}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Mobile tab pills */}
            <div className="md:hidden overflow-x-auto pb-2 -mx-4 px-4">
              <div className="flex gap-2 w-max">
                {faqCategories.map((cat, idx) => (
                  <button
                    key={cat.category}
                    onClick={() => handleCategoryChange(idx)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition-all duration-150
                      ${activeCategory === idx
                        ? 'bg-[#548B2E] text-white border-[#548B2E]'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#548B2E]'
                      }`}
                  >
                    <span className={activeCategory === idx ? 'text-white' : 'text-gray-400'}>
                      {categoryIcon(cat.category)}
                    </span>
                    {cat.category}
                  </button>
                ))}
              </div>
            </div>

            {/* FAQ panel */}
            <div className="flex-1 min-w-0">
              {/* Category header */}
              <div className="flex items-center gap-3 mb-6">
                <span className="p-2.5 rounded-xl bg-[#f1f8e9] text-[#548B2E]">
                  {categoryIcon(selected.category)}
                </span>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{selected.category}</h2>
                  <p className="text-sm text-gray-400">{selected.faqs.length} questions in this section</p>
                </div>
              </div>

              {/* Accordion */}
              <div className="space-y-3">
                {selected.faqs.map((faq, faqIndex) => {
                  const isOpen = openIndex === faqIndex;
                  return (
                    <div
                      key={faq.question}
                      className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden
                        ${isOpen ? 'border-[#548B2E] shadow-sm' : 'border-gray-100 shadow-sm'}`}
                    >
                      <button
                        onClick={() => toggleFaq(faqIndex)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-150"
                      >
                        <span className="text-sm font-semibold text-gray-800 pr-4">{faq.question}</span>
                        <span className="flex-shrink-0 text-[#548B2E]">
                          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </span>
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                      >
                        <div className="px-6 pb-5 pt-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100">
                          {faq.answer}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Category nav footer */}
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => handleCategoryChange(Math.max(0, activeCategory - 1))}
                  disabled={activeCategory === 0}
                  className="text-sm font-medium text-[#548B2E] disabled:text-gray-300 hover:underline transition-colors"
                >
                  ← Previous topic
                </button>
                <span className="text-xs text-gray-400">{activeCategory + 1} / {faqCategories.length}</span>
                <button
                  onClick={() => handleCategoryChange(Math.min(faqCategories.length - 1, activeCategory + 1))}
                  disabled={activeCategory === faqCategories.length - 1}
                  className="text-sm font-medium text-[#548B2E] disabled:text-gray-300 hover:underline transition-colors"
                >
                  Next topic →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-gray-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">
            Our team is happy to help. Reach out and we'll get back to you promptly.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="inline-block bg-[#548B2E] hover:bg-[#3f6b20] text-white px-10 py-4 font-bold text-lg rounded transition-colors duration-300"
            >
              Contact Us
            </Link>
            <a
              href="mailto:info@bookmyfresh.com"
              className="inline-block bg-transparent border-2 border-white hover:bg-white hover:text-gray-800 text-white px-10 py-4 font-bold text-lg rounded transition-colors duration-300"
            >
              Email Us
            </a>
          </div>
          <p className="mt-6 text-gray-400 text-sm">
            Or call us at{' '}
            <a href="tel:+918078622096" className="text-[#bddd97] hover:underline">+91-8078622096</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
